package dev.lingvo.site.components.editor

import androidx.compose.runtime.Stable
import androidx.compose.runtime.mutableStateMapOf
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.SourceChangeDto
import dev.lingvo.shared.SourceSnapshotDto
import dev.lingvo.shared.isCanonicalSourceReviewId
import dev.lingvo.site.api.API_JSON
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.source.hasIdentity
import dev.lingvo.site.components.source.validCurrentSource
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import dev.lingvo.site.i18n.t

@Stable
internal class EditorSourceReviewStore(
    private val projectSlug: String,
    private val locale: String,
    private val scope: CoroutineScope,
    private val targetWasPresent: (String) -> Boolean,
    private val rowSource: (String) -> SourceSnapshotDto?,
    private val onRead: (String, SourceChangeDto) -> Unit,
    private val onReadFailure: (String) -> Unit,
) {
    val changes = mutableStateMapOf<String, Async<SourceChangeDto>>()
    val pauses = mutableStateMapOf<String, String>()
    val pauseCodes = mutableStateMapOf<String, String>()
    val targetAbsent = mutableStateMapOf<String, Boolean>()
    val reaffirming = mutableStateMapOf<String, Boolean>()
    private val generations = mutableMapOf<String, Int>()

    fun ensureLoaded(row: KeyRow, retry: Boolean = false, onLoaded: (SourceChangeDto) -> Unit = {}) {
        ensureKeyLoaded(row.key.id, retry, onLoaded)
    }

    private fun ensureKeyLoaded(keyId: String, retry: Boolean = false, onLoaded: (SourceChangeDto) -> Unit = {}) {
        if (!Session.serverFeatures.sourceAwareReview || (!retry && changes[keyId] != null)) return
        val generation = (generations[keyId] ?: 0) + 1
        generations[keyId] = generation
        changes[keyId] = Async.Loading
        scope.launch {
            val result = try {
                val response = ApiClient.sourceChange(projectSlug, keyId, locale)
                if (!response.hasIdentity(keyId, locale)) throw unavailableSourceReview()
                if (generations[keyId] == generation) observeTarget(keyId, response, targetWasPresent(keyId))
                Async.Success(response)
            } catch (cancelled: CancellationException) {
                if (generations[keyId] == generation) changes.remove(keyId)
                throw cancelled
            } catch (failure: Throwable) {
                if (generations[keyId] == generation && (failure as? ApiException)?.status in listOf(404, 501)) pauses[keyId] = unavailableSourceReviewMessage()
                Async.Failure(failure.message ?: unavailableSourceReviewMessage(), (failure as? ApiException)?.status)
            }
            if (generations[keyId] == generation) {
                changes[keyId] = result
                if (result is Async.Success) {
                    onRead(keyId, result.value)
                    onLoaded(result.value)
                } else if (result is Async.Failure) onReadFailure(keyId)
            }
        }
    }

    fun loaded(keyId: String): SourceChangeDto? = (changes[keyId] as? Async.Success)?.value

    fun refused(keyId: String, failure: ApiException, targetWasPresent: Boolean = true) {
        if (failure.status !in listOf(400, 422)) {
            pauses[keyId] = if (failure.status in listOf(404, 501)) unavailableSourceReviewMessage() else failure.message
            pauseCodes[keyId] = failure.code
        }
        failure.details["sourceChange"]?.let { element ->
            val change = runCatching { API_JSON.decodeFromJsonElement(SourceChangeDto.serializer(), element) }.getOrNull()
            if (change?.hasIdentity(keyId, locale) == true) {
                if (changes[keyId] is Async.Loading) return@let
                val known = loaded(keyId)
                val source = change.validCurrentSource()
                val latest = listOfNotNull(known?.validCurrentSource(), rowSource(keyId))
                    .maxByOrNull { it.view.sourceVersion }
                if (source != null && latest != null) {
                    if (latest.view.sourceVersion > source.view.sourceVersion) {
                        if (known?.validCurrentSource()?.view != latest.view) ensureKeyLoaded(keyId, retry = true)
                        return@let
                    }
                    if (latest.view.sourceVersion == source.view.sourceVersion && latest.view != source.view ||
                        known?.validCurrentSource()?.view == source.view && known?.target?.revision != change.target?.revision
                    ) {
                        // Different target UUIDs establish a conflict, never an ordering.
                        ensureKeyLoaded(keyId, retry = true)
                        return@let
                    }
                }
                changes[keyId] = Async.Success(change)
                if (failure.status == 409) observeTarget(keyId, change, targetWasPresent)
            }
        }
    }

    private fun observeTarget(keyId: String, change: SourceChangeDto, previouslyPresent: Boolean) {
        if (change.validCurrentSource() == null) return
        val target = change.target
        if (target == null && previouslyPresent) targetAbsent[keyId] = true
        else if (target != null && isCanonicalSourceReviewId(target.revision.orEmpty())) targetAbsent.remove(keyId)
    }

    fun canReview(keyId: String): Boolean = Session.serverFeatures.sourceAwareReview &&
        loaded(keyId)?.let { change ->
            change.validCurrentSource() != null && (change.target == null || isCanonicalSourceReviewId(change.target?.revision.orEmpty()))
        } == true

    fun runReaffirm(keyId: String, action: suspend () -> Unit) {
        if (reaffirming[keyId] == true) return
        reaffirming[keyId] = true
        scope.launch {
            try { action() } finally { reaffirming.remove(keyId) }
        }
    }
}

internal fun unavailableSourceReviewMessage(): String = tr(
    "editor.editor_source_review_store.source_aware_review_is_unavailable_reload",
    "Source-aware review is unavailable. Reload on a supported server before using this action; your draft is kept.",
)

internal fun unavailableSourceReview(): ApiException = ApiException(0, "source_aware_unavailable", unavailableSourceReviewMessage())
